import {
  Button,
  Divider,
  Grid,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconEdit, IconPrinter, IconTrash } from "@tabler/icons-react";
import { purchaseOrderApi } from "apis/purchaseOrderApi";
import { Loading, PageHeader } from "components";
import { useAbility } from "configs/casl";
import PageContent from "components/layouts/PageContent";
import CustomTable from "components/ui/CustomTable";
import CustomBadge from "components/ui/CustomBadge/CustomBadge";
import CustomText from "components/ui/CustomText/CustomText";
import { MRT_ColumnDef } from "mantine-react-table";
import {
  PURCHASE_ORDER_STATUS_COLOR,
  PURCHASE_ORDER_STATUS_LABEL,
} from "constants/common/purchaseOrder";
import { PurchaseOrderItemResponseType } from "constants/types/purchaseOrder";
import { ReactNode, useMemo } from "react";
import { formatCurrency, formatDate } from "utils/helpers/format";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PURCHASE_ORDER_PAGE } from "routes/route.constant";
import { usePurchaseOrderDetail } from "utils/hooks/request/usePurchaseOrder";
import { useAppModal } from "utils/hooks/ui/useAppModal";
import { useUserMap } from "utils/hooks/request/useUsers";
import { PURCHASE_ORDER_PAGE_TITLE } from "../config";
import PurchaseOrderPrintContent from "pages/PurchaseOrder/components/PurchaseOrderPrint";
import PurchaseOrderCreateForm from "../CreatePurchaseOrder/components/PurchaseOrderCreateForm/PurchaseOrderCreateForm";
import { useNotification } from "utils/hooks";

// ─── sub-components ─────────────────────────────────────────────────────────

const InfoField = ({ label, value }: { label: string; value: ReactNode }) => (
  <Stack gap={2}>
    <Text size="xs" c="dimmed">
      {label}
    </Text>
    <Text size="sm" fw={500}>
      {value ?? "—"}
    </Text>
  </Stack>
);

const SectionTitle = ({ children }: { children: string }) => (
  <CustomText variant="section-title">{children}</CustomText>
);

// ─── items table ─────────────────────────────────────────────────────────────

const ItemsTable = ({
  items,
  totalAmount,
}: {
  items: PurchaseOrderItemResponseType[];
  totalAmount: number;
}) => {
  const columns = useMemo<MRT_ColumnDef<PurchaseOrderItemResponseType>[]>(
    () => [
      {
        id: "stt",
        header: "STT",
        size: 60,
        grow: false,
      },
      {
        accessorKey: "productName",
        header: "Tên sản phẩm",
        minSize: 260,
        grow: true,
        Cell: ({ cell }) => (
          <Text size="sm" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            {cell.getValue<string>()}
          </Text>
        ),
      },
      {
        accessorKey: "productUnit",
        header: "Đơn vị",
        size: 100,
        grow: false,
      },
      {
        accessorKey: "quantity",
        header: "Số lượng",
        size: 100,
        grow: false,
        mantineTableBodyCellProps: { align: "right" },
        Cell: ({ cell }) => cell.getValue<number>().toLocaleString("vi-VN"),
      },
      {
        accessorKey: "unitPrice",
        header: "Đơn giá",
        size: 130,
        grow: false,
        Cell: ({ cell }) => formatCurrency(cell.getValue<number>()),
        Footer: () => (
          <Text size="sm" fw={600}>
            Tổng cộng
          </Text>
        ),
      },
      {
        accessorKey: "totalAmount",
        header: "Thành tiền",
        size: 140,
        grow: false,
        Cell: ({ cell }) => (
          <Text size="sm" fw={500}>
            {formatCurrency(cell.getValue<number>())}
          </Text>
        ),
        Footer: () => (
          <Text size="sm" fw={700} c="blue">
            {formatCurrency(totalAmount)}
          </Text>
        ),
      },
    ],
    [totalAmount],
  );

  return <CustomTable columns={columns} data={items} />;
};

// ─── page ────────────────────────────────────────────────────────────────────

const DetailPurchaseOrder = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { purchaseOrder, isLoading, mutate } = usePurchaseOrderDetail(id);
  const { openCrudModal } = useAppModal();
  const ability = useAbility();
  const { notification } = useNotification();
  const canExport = ability.can("export", "purchase_order");
  const canUpdate = ability.can("update", "purchase_order");
  const canDelete = ability.can("delete", "purchase_order");
  const isEditMode = canUpdate && searchParams.get("mode") === "edit";
  const userMap = useUserMap();

  const handlePrint = () => {
    if (!purchaseOrder) return;
    openCrudModal(
      <CustomText variant="section-title">Phiếu mua hàng</CustomText>,
      {
        mode: "view",
        children: <PurchaseOrderPrintContent purchaseOrder={purchaseOrder} />,
      },
      "90%",
    );
  };

  const handleDelete = () => {
    if (!purchaseOrder) return;
    modals.openConfirmModal({
      title: "Xóa đơn mua hàng",
      children: (
        <Text size="sm">
          Bạn có chắc chắn muốn xóa đơn mua hàng{" "}
          <Text component="span" fw={600}>
            {purchaseOrder.code}
          </Text>{" "}
          không?
        </Text>
      ),
      labels: { confirm: "Xóa", cancel: "Đóng" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await purchaseOrderApi.delete(purchaseOrder.id);
          notification.success({ title: "Xóa đơn mua hàng thành công" });
          navigate(PURCHASE_ORDER_PAGE);
        } catch (error: any) {
          notification.error({
            title: "Xóa đơn mua hàng thất bại",
            message: error?.error?.message,
          });
        }
      },
    });
  };

  return (
    <Stack gap="sm">
      <PageHeader
        breadcrumbs={[
          { title: PURCHASE_ORDER_PAGE_TITLE, href: PURCHASE_ORDER_PAGE },
          { title: "Chi tiết đơn mua hàng", href: "" },
        ]}
      />

      {(canExport || canUpdate || canDelete) && (
        <Group justify="flex-end">
          {canUpdate && (
            <Button
              leftSection={<IconEdit size={16} />}
              variant={isEditMode ? "filled" : "default"}
              disabled={!purchaseOrder}
              onClick={() =>
                navigate({
                  search: isEditMode ? "" : "mode=edit",
                })
              }
            >
              {isEditMode ? "Đang sửa" : "Sửa đơn"}
            </Button>
          )}
          {canExport && (
            <Button
              leftSection={<IconPrinter size={16} />}
              variant="default"
              disabled={!purchaseOrder}
              onClick={handlePrint}
            >
              In / Xuất PDF
            </Button>
          )}
          {canDelete && (
            <Button
              leftSection={<IconTrash size={16} />}
              color="red"
              variant="default"
              disabled={!purchaseOrder}
              onClick={handleDelete}
            >
              Xóa đơn
            </Button>
          )}
        </Group>
      )}

      <PageContent>
        <Loading variant="overlay" visible={isLoading}>
          {purchaseOrder && isEditMode ? (
            <PurchaseOrderCreateForm
              mode="edit"
              purchaseOrder={purchaseOrder}
              onSaved={() => {
                mutate();
                navigate({ search: "" });
              }}
            />
          ) : (
            purchaseOrder && (
              <Stack gap="md">
                {/* ── Thông tin chung ── */}
                <Paper p="md" withBorder>
                  <Stack gap="md">
                    <SectionTitle>Thông tin đơn hàng</SectionTitle>

                    <SimpleGrid cols={{ base: 2, md: 3 }}>
                      <InfoField
                        label="Mã đơn hàng"
                        value={purchaseOrder.code}
                      />
                      <InfoField
                        label="Kho nhập"
                        value={purchaseOrder.warehouse.name}
                      />
                      <InfoField
                        label="Trạng thái"
                        value={
                          <CustomBadge
                            label={
                              PURCHASE_ORDER_STATUS_LABEL[
                                purchaseOrder.status
                              ] ?? purchaseOrder.status
                            }
                            color={
                              PURCHASE_ORDER_STATUS_COLOR[
                                purchaseOrder.status
                              ] ?? "gray"
                            }
                          />
                        }
                      />
                      <InfoField
                        label="Tổng tiền"
                        value={
                          <Text size="sm" fw={700} c="blue">
                            {formatCurrency(purchaseOrder.totalAmount)}
                          </Text>
                        }
                      />
                      <InfoField
                        label="Người tạo"
                        value={purchaseOrder.createdBy.fullname}
                      />
                      <InfoField
                        label="Ngày tạo"
                        value={formatDate(purchaseOrder.createdAt)}
                      />
                    </SimpleGrid>

                    {purchaseOrder.notes && (
                      <>
                        <Divider />
                        <Grid>
                          <Grid.Col span={{ base: 12, md: 8 }}>
                            <InfoField
                              label="Ghi chú"
                              value={purchaseOrder.notes}
                            />
                          </Grid.Col>
                        </Grid>
                      </>
                    )}
                  </Stack>
                </Paper>

                {/* ── Thông tin nhà cung cấp ── */}
                <Paper p="md" withBorder>
                  <Stack gap="md">
                    <SectionTitle>Thông tin nhà cung cấp</SectionTitle>

                    <SimpleGrid cols={{ base: 2, md: 3 }}>
                      <InfoField
                        label="Tên nhà cung cấp"
                        value={purchaseOrder.partner.name}
                      />
                      <InfoField
                        label="Mã nhà cung cấp"
                        value={purchaseOrder.partner.code}
                      />
                      <InfoField
                        label="Số điện thoại"
                        value={purchaseOrder.partner.phone}
                      />
                      <InfoField
                        label="Mã số thuế"
                        value={purchaseOrder.partner.taxCode}
                      />
                      <InfoField
                        label="Người phụ trách"
                        value={
                          purchaseOrder.partner.contactPersonId
                            ? (userMap[purchaseOrder.partner.contactPersonId]
                                ?.fullname ??
                              purchaseOrder.partner.contactPerson)
                            : purchaseOrder.partner.contactPerson
                        }
                      />
                      <InfoField
                        label="Trạng thái NCC"
                        value={
                          <CustomBadge
                            label={
                              purchaseOrder.partner.isActive
                                ? "Đang hoạt động"
                                : "Tạm khóa"
                            }
                            color={
                              purchaseOrder.partner.isActive ? "green" : "red"
                            }
                          />
                        }
                      />
                    </SimpleGrid>
                  </Stack>
                </Paper>

                {/* ── Danh sách sản phẩm ── */}
                <Paper p="md" withBorder>
                  <Stack gap="md">
                    <SectionTitle>Danh sách sản phẩm</SectionTitle>

                    {purchaseOrder.items && purchaseOrder.items.length > 0 ? (
                      <ItemsTable
                        items={purchaseOrder.items}
                        totalAmount={purchaseOrder.totalAmount}
                      />
                    ) : (
                      <Text size="sm" c="dimmed">
                        Không có sản phẩm nào.
                      </Text>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            )
          )}
        </Loading>
      </PageContent>
    </Stack>
  );
};

export default DetailPurchaseOrder;
